import type { SelectHTMLAttributes } from 'react';
import { DISABLED, FOCUS_RING, HOVER_OUTLINE } from './states';

/**
 * Liste déroulante habillée (US-25). Les `<select>` natifs mesuraient 29 px de
 * haut sur la fiche, sous le plancher de 44 px du §4.
 *
 * Le `<select>` natif est **conservé** sous l'habillage plutôt que remplacé
 * par une liste ARIA maison. Une liste maison, c'est la gestion du clavier, du
 * défilement, du repli au clic extérieur et de l'annonce vocale à réécrire, et
 * sur mobile la perte du sélecteur du système — qui est précisément le meilleur
 * contrôle disponible. On garde donc l'élément natif et on lui donne la taille
 * et les états qui lui manquaient (§5 CLAUDE.md : pas d'abstraction anticipée).
 *
 * `appearance-none` retire la flèche du système, redessinée en dessous pour
 * qu'elle ait la même allure d'un navigateur à l'autre.
 *
 * Le mode cuisine, lui, n'en utilise aucun : §4 y proscrit les menus déroulants.
 */
export type SelectSize = 'compact' | 'default';

const SIZE_CLASSES: Record<SelectSize, string> = {
  compact: 'min-h-touch-min pl-3 pr-9 text-sm',
  default: 'min-h-touch pl-3 pr-9 text-sm',
};

// `size` est déjà un attribut HTML de `<select>` (nombre de lignes visibles) :
// la densité s'appelle donc `density`, comme sur `Field`, plutôt que de
// masquer un attribut natif par un type incompatible.
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  density?: SelectSize;
}

export function Select({ density = 'default', className = '', children, ...props }: SelectProps): React.JSX.Element {
  return (
    <span className="relative inline-flex items-center">
      <select
        className={`w-full appearance-none rounded-control border border-border-interactive bg-transparent text-foreground ${SIZE_CLASSES[density]} ${HOVER_OUTLINE} ${FOCUS_RING} ${DISABLED} ${className}`.trim()}
        {...props}
      >
        {children}
      </select>
      {/* `pointer-events-none` : la flèche est un dessin, le clic doit
          traverser jusqu'au `<select>` qui est dessous. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 16 16"
        className="pointer-events-none absolute right-3 size-4 text-foreground-secondary"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="m4 6 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
